import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import {
  useGetHaveListDefaultsQuery,
  useUpdateHaveListDefaultsMutation,
} from 'store/services/settings/settingsApi';
import {
  useToggleAlertEnabledMutation,
  useBatchUpdateHaveListMutation,
} from 'store/services/collectionList/haveList/haveListApi';
import { useCanUseHaveListAlerts } from 'hooks/useCanUseHaveListAlerts';
import { getMonthlyAlertLimit } from 'constants/haveListAlerts';
import {
  isPhoneNotVerifiedError,
  isFeatureNotAvailableError,
} from 'utils/haveListAlertErrors';
import { showError } from 'utils/toastNotifications';

type PendingToggle =
  | { kind: 'one'; recordId: number; enable: boolean }
  | { kind: 'many'; ids: number[]; enable: boolean };

export interface UseHaveListAlertToggleResult {
  canUseFeature: boolean;
  phoneVerified: boolean;
  isElite: boolean;
  sent: number;
  limit: number;
  remaining: number;
  toggleOne: (recordId: number, enable: boolean) => Promise<void>;
  toggleMany: (
    ids: number[],
    enable: boolean,
  ) => Promise<{ successCount: number; totalCount: number } | undefined>;
  setDefaultAlert: (enable: boolean) => Promise<void>;
  verifyPromptProps: {
    open: boolean;
    onClose: () => void;
    onVerificationSuccess: () => void;
  };
}

export function useHaveListAlertToggle(): UseHaveListAlertToggleResult {
  const dispatch = useDispatch();
  const canUseFeature = useCanUseHaveListAlerts();
  const { data: user } = useGetMeQuery();
  const { data: defaults } = useGetHaveListDefaultsQuery(undefined, {
    skip: !canUseFeature,
  });
  const [toggleAlert] = useToggleAlertEnabledMutation();
  const [batchUpdate] = useBatchUpdateHaveListMutation();
  const [updateDefaults] = useUpdateHaveListDefaultsMutation();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);

  const phoneVerified = !!user?.phoneVerified;
  const isElite = !!user?.isElite;
  const sent = defaults?.alertsSentThisMonth ?? 0;
  const limit =
    defaults?.alertsMonthlyLimit ??
    getMonthlyAlertLimit(isElite, user?.phoneNumber ?? null);
  const remaining = Math.max(0, limit - sent);

  // Runs the mutation directly, skipping the phone-verified gate.
  // Used for the verification-success replay path where we know phone was just verified.
  const fireToggleOne = useCallback(
    async (recordId: number, enable: boolean): Promise<void> => {
      try {
        await toggleAlert({ haveListRecordId: recordId, alertEnabled: enable }).unwrap();
      } catch (err) {
        if (isFeatureNotAvailableError(err)) {
          console.warn(
            '[useHaveListAlertToggle] FEATURE_NOT_AVAILABLE on replay — admin gating may be misaligned',
            err,
          );
          return;
        }
        showError('Failed to update alert preference');
      }
    },
    [toggleAlert],
  );

  const fireToggleMany = useCallback(
    async (
      ids: number[],
      enable: boolean,
    ): Promise<{ successCount: number; totalCount: number } | undefined> => {
      if (ids.length === 0) return undefined;
      try {
        const result = await batchUpdate({ ids, changes: { alertEnabled: enable } }).unwrap();
        return { successCount: result.successCount, totalCount: result.totalCount };
      } catch (err) {
        if (isFeatureNotAvailableError(err)) {
          console.warn(
            '[useHaveListAlertToggle] FEATURE_NOT_AVAILABLE on replay — admin gating may be misaligned',
            err,
          );
          return undefined;
        }
        showError('Failed to apply batch alert change');
        return undefined;
      }
    },
    [batchUpdate],
  );

  const toggleOne = useCallback(
    async (recordId: number, enable: boolean): Promise<void> => {
      if (!canUseFeature) return;
      if (enable && !phoneVerified) {
        setPendingToggle({ kind: 'one', recordId, enable });
        setVerifyOpen(true);
        return;
      }
      try {
        await toggleAlert({ haveListRecordId: recordId, alertEnabled: enable }).unwrap();
      } catch (err) {
        if (isPhoneNotVerifiedError(err)) {
          // Backend rejected — server-side phone state must have changed mid-session.
          setPendingToggle({ kind: 'one', recordId, enable });
          setVerifyOpen(true);
          return;
        }
        if (isFeatureNotAvailableError(err)) {
          console.warn(
            '[useHaveListAlertToggle] FEATURE_NOT_AVAILABLE on toggleOne — admin gating may be misaligned',
            err,
          );
          return;
        }
        showError('Failed to update alert preference');
      }
    },
    [canUseFeature, phoneVerified, toggleAlert],
  );

  const toggleMany = useCallback(
    async (
      ids: number[],
      enable: boolean,
    ): Promise<{ successCount: number; totalCount: number } | undefined> => {
      if (!canUseFeature || ids.length === 0) return undefined;
      if (enable && !phoneVerified) {
        setPendingToggle({ kind: 'many', ids, enable });
        setVerifyOpen(true);
        return undefined;
      }
      try {
        const result = await batchUpdate({
          ids,
          changes: { alertEnabled: enable },
        }).unwrap();
        return { successCount: result.successCount, totalCount: result.totalCount };
      } catch (err) {
        if (isPhoneNotVerifiedError(err)) {
          setPendingToggle({ kind: 'many', ids, enable });
          setVerifyOpen(true);
          showError('Could not apply alert change — verify your phone first.');
          return undefined;
        }
        if (isFeatureNotAvailableError(err)) {
          console.warn(
            '[useHaveListAlertToggle] FEATURE_NOT_AVAILABLE on toggleMany — admin gating may be misaligned',
            err,
          );
          return undefined;
        }
        showError('Failed to apply batch alert change');
        return undefined;
      }
    },
    [canUseFeature, phoneVerified, batchUpdate],
  );

  const setDefaultAlert = useCallback(
    async (enable: boolean): Promise<void> => {
      if (!canUseFeature) return;
      if (enable && !phoneVerified) {
        setVerifyOpen(true);
        return;
      }
      try {
        await updateDefaults({ defaultHaveListAlert: enable }).unwrap();
      } catch (err) {
        if (isPhoneNotVerifiedError(err)) {
          setVerifyOpen(true);
          return;
        }
        if (isFeatureNotAvailableError(err)) {
          console.warn(
            '[useHaveListAlertToggle] FEATURE_NOT_AVAILABLE on setDefaultAlert — admin gating may be misaligned',
            err,
          );
          return;
        }
        showError('Failed to update default alert preference');
      }
    },
    [canUseFeature, phoneVerified, updateDefaults],
  );

  const handleClose = useCallback(() => {
    // User dismissed without verifying — drop the pending intent so it doesn't replay
    // when they next open the modal.
    setVerifyOpen(false);
    setPendingToggle(null);
  }, []);

  const handleVerified = useCallback(() => {
    // Refresh User cache so phoneVerified flips to true across all subscribers.
    dispatch(meApi.util.invalidateTags(['User']));
    setVerifyOpen(false);
    const toReplay = pendingToggle;
    setPendingToggle(null);
    if (!toReplay) return;
    if (toReplay.kind === 'one') {
      void fireToggleOne(toReplay.recordId, toReplay.enable);
    } else {
      void fireToggleMany(toReplay.ids, toReplay.enable);
    }
  }, [dispatch, pendingToggle, fireToggleOne, fireToggleMany]);

  return {
    canUseFeature,
    phoneVerified,
    isElite,
    sent,
    limit,
    remaining,
    toggleOne,
    toggleMany,
    setDefaultAlert,
    verifyPromptProps: {
      open: verifyOpen,
      onClose: handleClose,
      onVerificationSuccess: handleVerified,
    },
  };
}
